/**
 * Tiny analyser over the remote bot MediaStreamTrack (playback path).
 * Levels reflect RTP audio energy arriving at the client — useful to tell
 * "bot is sending audio" vs "speakers/tab muted" when chat still works.
 */

const BAR_COUNT = 4;
type Listener = (levels: number[]) => void;

let listeners = new Set<Listener>();
let ctx: AudioContext | null = null;
let source: MediaStreamAudioSourceNode | null = null;
let analyser: AnalyserNode | null = null;
let raf = 0;
let boundTrackId: string | null = null;
const levels = new Array(BAR_COUNT).fill(0);
const data = new Uint8Array(64);

function emit() {
  const snapshot = levels.slice();
  listeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch {
      /* ignore */
    }
  });
}

function tick() {
  raf = 0;
  if (!analyser) return;
  analyser.getByteTimeDomainData(data);
  // Split buffer into BAR_COUNT windows; RMS → 0..1 with light smoothing.
  const slice = Math.floor(data.length / BAR_COUNT);
  for (let i = 0; i < BAR_COUNT; i++) {
    let sum = 0;
    const start = i * slice;
    for (let j = 0; j < slice; j++) {
      const v = (data[start + j] - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / Math.max(1, slice));
    // Gain so conversational speech lights bars without needing peak clipping.
    const target = Math.min(1, rms * 4.2);
    levels[i] = levels[i] * 0.55 + target * 0.45;
  }
  emit();
  raf = requestAnimationFrame(tick);
}

function ensureLoop() {
  if (!analyser || raf) return;
  raf = requestAnimationFrame(tick);
}

export function subscribeBotPlaybackLevels(fn: Listener): () => void {
  listeners.add(fn);
  fn(levels.slice());
  return () => {
    listeners.delete(fn);
  };
}

export function bindBotPlaybackTrack(track: MediaStreamTrack | null | undefined): void {
  if (!track || track.readyState === 'ended') {
    unbindBotPlaybackTrack();
    return;
  }
  const id = track.id;
  if (boundTrackId === id && analyser) {
    ensureLoop();
    return;
  }
  unbindBotPlaybackTrack();
  try {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    ctx = new AC();
    const stream = new MediaStream([track]);
    source = ctx.createMediaStreamSource(stream);
    analyser = ctx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.65;
    source.connect(analyser);
    // Do not connect to destination — HTMLAudioElement owns audible playback.
    boundTrackId = id;
    if (ctx.state === 'suspended') {
      void ctx.resume().catch(() => undefined);
    }
    ensureLoop();
    track.addEventListener('ended', unbindBotPlaybackTrack, { once: true });
  } catch {
    unbindBotPlaybackTrack();
  }
}

export function unbindBotPlaybackTrack(): void {
  if (raf) {
    cancelAnimationFrame(raf);
    raf = 0;
  }
  try {
    source?.disconnect();
  } catch {
    /* ignore */
  }
  try {
    analyser?.disconnect();
  } catch {
    /* ignore */
  }
  source = null;
  analyser = null;
  if (ctx) {
    void ctx.close().catch(() => undefined);
    ctx = null;
  }
  boundTrackId = null;
  for (let i = 0; i < BAR_COUNT; i++) levels[i] = 0;
  emit();
}

export function botPlaybackBarCount(): number {
  return BAR_COUNT;
}

/** Call from a user gesture (e.g. Tap to enable sound) so the analyser can run. */
export function resumeBotPlaybackMeter(): void {
  if (ctx?.state === 'suspended') {
    void ctx.resume().catch(() => undefined);
  }
  ensureLoop();
}
